import React, { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import { chartAnchor, registerAnchorConsumer } from '../chartAnchor';
import { useLivePnL } from '../hooks/useLivePnL';
import type { PriceConfig } from '../hooks/usePriceConfig';
import { selectActiveTradeVisual, useTradingStore } from '../store';

/**
 * SpeedTrading visual mechanics, wired to the live position.
 */

/**
 * Pulsing viewport frame — lime while the open position is green, pink
 * while it's red. Held for the life of the position (unlike the 2s
 * TradeResultOverlay, which fires on close).
 */
export const WinFrame: React.FC = () => {
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const currentValue = useTradingStore((s) => s.currentValue);
    const { livePnLValue } = useLivePnL(currentValue);

    if (!activePosition) return null;

    return (
        <div
            aria-hidden="true"
            className={cn(
                'st-win-frame',
                livePnLValue >= 0 ? 'st-win-frame--win' : 'st-win-frame--loss',
            )}
        />
    );
};

interface PnlTagProps {
    priceConfig: PriceConfig;
}

/**
 * The tracking pip: stands in for the chart's built-in price-capture box
 * and in-chart PnL badge (both hidden via CSS). Rides the line tip by
 * mirroring the `.cursor-reticle` position (the same spring-animated
 * anchor the old label used) each frame, flipping to the left side when
 * it would overflow the chart edge; falls back to the CSS corner position
 * when no reticle exists (candles mode). Always shows the live price;
 * while a position is open it adds the LONG/SHORT side chip and the
 * leverage-aware PnL (via useLivePnL, per price tick).
 */
export const PnlTag: React.FC<PnlTagProps> = ({ priceConfig }) => {
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const currentValue = useTradingStore((s) => s.currentValue);
    const { livePnLValue, livePnLPercent } = useLivePnL(currentValue);

    const tagRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = tagRef.current;
        const host = el?.parentElement;
        if (!el || !host) return;

        // Cache the host box and the tag's own size via ResizeObserver so
        // positioning never calls getBoundingClientRect (which would force a
        // synchronous reflow against the 60fps chart).
        let hostW = host.clientWidth;
        let hostH = host.clientHeight;
        let tagW = el.offsetWidth;
        let tagH = el.offsetHeight;
        const hostRo = new ResizeObserver(() => {
            hostW = host.clientWidth;
            hostH = host.clientHeight;
        });
        const tagRo = new ResizeObserver(() => {
            tagW = el.offsetWidth;
            tagH = el.offsetHeight;
        });
        hostRo.observe(host);
        tagRo.observe(el);

        // Positioned SYNCHRONOUSLY from the chart's render pass (the chart
        // calls setChartAnchor → this consumer), frame-locked to the line
        // tip with no trailing rAF. Movement is done with a GPU-composited
        // translate3d + will-change (compositor-only — no per-frame layout
        // or paint), the layout-affecting props are written ONCE on entering
        // tracking, and a move-guard skips writes when the tip hasn't moved.
        // All inline so a React re-render (rewrites className ~15Hz) can't
        // reset it. Works for both chart types: Live drives this at 60Hz,
        // Candles at 10Hz — both just translate a promoted layer.
        let trackingActive = false;
        let lastTx = Number.NaN;
        let lastTy = Number.NaN;
        const position = (x: number, y: number, valid: boolean) => {
            if (!valid) {
                if (trackingActive) {
                    el.style.transform = '';
                    el.style.left = '';
                    el.style.top = '';
                    el.style.right = '';
                    el.style.willChange = '';
                    el.style.animation = '';
                    el.style.alignItems = '';
                    trackingActive = false;
                    lastTx = Number.NaN;
                    lastTy = Number.NaN;
                }
                return;
            }
            const gap = 16;
            const fitsRight = x + gap + tagW <= hostW - 8;
            const tx = Math.min(
                Math.max(8, fitsRight ? x + gap : x - gap - tagW),
                Math.max(8, hostW - tagW - 8),
            );
            // Anchor the tag by its overall vertical middle so the price
            // line hits the pip's center.
            const ty = Math.min(
                Math.max(8, y - tagH / 2),
                Math.max(8, hostH - tagH - 8),
            );
            if (!trackingActive) {
                // One-time layout setup: origin at the container's top-left
                // (so translate3d places the tag), promote to its own layer,
                // and replicate the tracking look inline.
                el.style.left = '0px';
                el.style.top = '0px';
                el.style.right = 'auto';
                el.style.willChange = 'transform';
                el.style.animation = 'none';
                el.style.alignItems = 'flex-start';
                trackingActive = true;
            }
            // Move-guard: only touch transform (compositor-only) when the
            // tip actually moved ≥0.5px. No layout, no paint on a still tip.
            // The !isFinite check forces the FIRST write (lastTx starts NaN,
            // and any comparison against NaN is false).
            if (
                !Number.isFinite(lastTx) ||
                Math.abs(tx - lastTx) >= 0.5 ||
                Math.abs(ty - lastTy) >= 0.5
            ) {
                el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
                lastTx = tx;
                lastTy = ty;
            }
        };

        registerAnchorConsumer(position);
        // Apply the last known anchor immediately (chart may already be
        // running before this mounts).
        if (chartAnchor.valid) {
            position(chartAnchor.x, chartAnchor.y, true);
        }
        return () => {
            registerAnchorConsumer(null);
            hostRo.disconnect();
            tagRo.disconnect();
        };
    }, []);

    const winning = livePnLValue >= 0;
    const sign = winning ? '+' : '';
    // Truly nothing to show yet (no price, no position) — keep the element
    // mounted (so the effect's ref/consumer stay live) but hidden.
    const hidden = currentValue <= 0 && !activePosition;

    return (
        <div
            ref={tagRef}
            aria-hidden="true"
            className={cn(
                'st-pnl-tag',
                hidden && 'hidden',
                activePosition
                    ? winning
                        ? 'st-pnl-tag--win'
                        : 'st-pnl-tag--loss'
                    : 'st-pnl-tag--idle',
            )}
        >
            {activePosition && (
                <span
                    className={cn(
                        'st-pnl-tag__side',
                        activePosition.type === 'LONG'
                            ? 'st-pnl-tag__side--long'
                            : 'st-pnl-tag__side--short',
                    )}
                >
                    {activePosition.type} {activePosition.leverage}x
                </span>
            )}
            <span className="st-pnl-tag__price">
                ${priceConfig.formatDisplay(currentValue)}
            </span>
            {activePosition && (
                <>
                    <span className="st-pnl-tag__pct">
                        {sign}
                        {livePnLPercent.toFixed(2)}%
                    </span>
                    <span className="st-pnl-tag__usd">
                        {sign}${livePnLValue.toFixed(2)}
                    </span>
                </>
            )}
        </div>
    );
};
